import { useEffect, useState } from 'react';
import { Users, Shield, Zap, Key, Trash2 } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/store';
import type { RootState } from '@/store';
import { fetchUsers, deleteUser } from '@/store/thunks/userThunks';
import { fetchPompistes, deletePompiste } from '@/store/thunks/pompisteThunks';
import { fetchRoles, deleteRole } from '@/store/thunks/roleThunks';
import type { User } from '@/types';
import type { Pompiste } from '@/types';
import type { Role } from '@/types';
import { filterListByQuery } from '@/utils/filterList';
import Page from '@/components/Page';
import { AddButton, Button, FilterInput, Modal, DataTable, ConfirmModal, Tabs, TabPanel, TableActions } from '@/components/ui';
import UserForm from '@/components/users/UserForm';
import PompisteForm from '@/components/users/PompisteForm';
import RoleCard from '@/components/users/RoleCard';
import RoleForm from '@/components/users/RoleForm';
import RolePermissionsModal from '@/components/users/RolePermissionsModal';
import UserRolesModal from '@/components/users/UserRolesModal';

export default function UserPage() {
  const dispatch = useAppDispatch();
  const users = useAppSelector((s: RootState) => s.user.users);
  const pumpistes = useAppSelector((s: RootState) => s.pompiste.pumpistes);
  const roles = useAppSelector((s: RootState) => s.role.roles);
  const { isAdmin } = useAppSelector((s: RootState) => s.auth);
  const [tab, setTab] = useState(0);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [pompisteModalOpen, setPompisteModalOpen] = useState(false);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState<User | Pompiste | Role | null>(null);
  const [editingPompiste, setEditingPompiste] = useState<Pompiste | null>(null);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [roleForPermissions, setRoleForPermissions] = useState<Role | null>(null);
  const [userForRoles, setUserForRoles] = useState<User | null>(null);
  const [pompisteFilter, setPompisteFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  useEffect(() => {
    dispatch(fetchUsers());
    dispatch(fetchPompistes());
    dispatch(fetchRoles());
  }, [dispatch]);

  const list = users.filter((u) => !u.is_admin);
  const filteredPompistes = filterListByQuery(pumpistes, pompisteFilter, ['nom', 'prenom']);
  const filteredUsers = filterListByQuery(list, userFilter, ['code', 'login', 'nom', 'prenom']);
  const filteredRoles = filterListByQuery(roles, roleFilter, ['nom', 'code']);

  const userColumns = [
    { key: 'id', label: 'Id' },
    { key: 'code', label: 'Code' },
    { key: 'login', label: 'Login' },
    { key: 'nom', label: 'Nom' },
    { key: 'prenom', label: 'Prénom' },
  ];

  const pompisteColumns = [
    { key: 'id', label: 'Id' },
    { key: 'nom', label: 'Nom' },
    { key: 'prenom', label: 'Prénom' },
  ];

  const handleDeleteConfirm = () => {
    if (!toDelete) return;
    if ('login' in toDelete) dispatch(deleteUser((toDelete as User).id!));
    else if ('prenom' in toDelete) dispatch(deletePompiste(toDelete as Pompiste));
    else dispatch(deleteRole(toDelete as Role));
    setToDelete(null);
    setConfirmOpen(false);
  };

  return (
    <Page title="Utilisateurs">
      <Tabs
        tabs={[
          { label: 'Pompistes', icon: <Zap className="h-4 w-4" /> },
          { label: 'Utilisateurs', icon: <Users className="h-4 w-4" />, disabled: !isAdmin },
          { label: 'Rôles', icon: <Shield className="h-4 w-4" />, disabled: !isAdmin },
        ]}
        value={tab}
        onChange={setTab}
      />

      <TabPanel value={tab} index={0}>
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold">Liste des pompistes</h2>
              <FilterInput value={pompisteFilter} onChange={setPompisteFilter} placeholder="Rechercher (nom, prénom)…" id="pompiste-filter" />
            </div>
            <AddButton label="Créer un pompiste" onClick={() => { setEditingPompiste(null); setPompisteModalOpen(true); }} />
          </div>
          <DataTable<Pompiste>
            columns={pompisteColumns}
            data={filteredPompistes}
            emptyMessage="Aucun pompiste"
            actions={(row) => (
              <TableActions
                onEdit={() => { setEditingPompiste(row); setPompisteModalOpen(true); }}
                onDelete={() => { setToDelete(row); setConfirmOpen(true); }}
              />
            )}
          />
        </div>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold">Liste des utilisateurs</h2>
              <FilterInput value={userFilter} onChange={setUserFilter} placeholder="Rechercher (code, login, nom)…" id="user-filter" />
            </div>
            {isAdmin && <AddButton label="Créer un utilisateur" onClick={() => setUserModalOpen(true)} />}
          </div>
          <DataTable<User>
            columns={userColumns}
            data={filteredUsers}
            keyField="id"
            emptyMessage="Aucun utilisateur"
            actions={
              isAdmin
                ? (row) => (
                    <div className="flex justify-end gap-1">
                      <Button variant="outline" size="sm" onClick={() => setUserForRoles(row)} aria-label="Attribuer rôles" title="Attribuer rôles">
                        <Key className="h-4 w-4" />
                      </Button>
                      <Button variant="danger" size="sm" onClick={() => { setToDelete(row); setConfirmOpen(true); }} aria-label="Supprimer">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )
                : undefined
            }
          />
        </div>
      </TabPanel>

      <TabPanel value={tab} index={2}>
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold">Liste des rôles</h2>
              <FilterInput value={roleFilter} onChange={setRoleFilter} placeholder="Rechercher (nom, code)…" id="role-filter" />
            </div>
            <AddButton label="Créer un rôle" onClick={() => { setEditingRole(null); setRoleModalOpen(true); }} />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredRoles.map((role) => (
              <RoleCard
                key={role.id}
                role={role}
                onEdit={(r) => {
                  setEditingRole(r);
                  setRoleModalOpen(true);
                }}
                onDelete={(r) => {
                  setToDelete(r);
                  setConfirmOpen(true);
                }}
                onPermissions={(r) => setRoleForPermissions(r)}
              />
            ))}
          </div>
        </div>
      </TabPanel>

      <Modal open={userModalOpen} onClose={() => setUserModalOpen(false)} title="Créer un utilisateur">
        <UserForm onClose={() => setUserModalOpen(false)} onSuccess={() => setUserModalOpen(false)} />
      </Modal>

      <Modal open={pompisteModalOpen} onClose={() => setPompisteModalOpen(false)} title={editingPompiste ? 'Modifier le pompiste' : 'Nouveau pompiste'}>
        <PompisteForm
          pompiste={editingPompiste ?? undefined}
          type={editingPompiste ? 'update' : 'create'}
          onClose={() => setPompisteModalOpen(false)}
          onSuccess={() => setPompisteModalOpen(false)}
        />
      </Modal>

      <Modal open={roleModalOpen} onClose={() => setRoleModalOpen(false)} title={editingRole ? 'Modifier le rôle' : 'Nouveau rôle'}>
        <RoleForm
          role={editingRole ?? undefined}
          type={editingRole ? 'update' : 'create'}
          onClose={() => setRoleModalOpen(false)}
          onSuccess={() => setRoleModalOpen(false)}
        />
      </Modal>

      <RolePermissionsModal
        role={roleForPermissions}
        open={roleForPermissions != null}
        onClose={() => setRoleForPermissions(null)}
        onSuccess={() => dispatch(fetchRoles())}
      />

      <UserRolesModal
        user={userForRoles}
        open={userForRoles != null}
        onClose={() => setUserForRoles(null)}
      />

      <ConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
        message="Voulez-vous supprimer cet élément ?"
      />
    </Page>
  );
}
